import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for managing content sections in lessons and modules.
 * Handles CRUD operations for multi-content sections. Supports:
 *   video, text, markdown, pdf, presentation, quiz, checklist, poll.
 */
const INITIAL_FORM = {
  title: '',
  type: 'markdown',
  order: 0,
  video_url: '',
  text_content: '',
  markdown_content: '',
  pdf_url: '',
  presentation_url: '',
  quiz_data: {
    passing_score: 70,
    questions: [],
  },
  checklist_data: {
    items: [],
    require_all: true,
  },
  poll_data: {
    question: '',
    options: [],
    allow_multiple: false,
  },
};

export const useContentSections = () => {
  const [contentSectionForm, setContentSectionForm] = useState({ ...INITIAL_FORM });
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const resetForm = () => setContentSectionForm({ ...INITIAL_FORM });

  // Add or update a content section
  const addContentSection = (currentSections) => {
    if (!contentSectionForm.title) {
      toast.error('Please enter a section title');
      return null;
    }

    if (editingSectionId) {
      const updatedSections = currentSections.map(section =>
        section.id === editingSectionId
          ? { ...contentSectionForm, id: editingSectionId }
          : section
      );
      setEditingSectionId(null);
      toast.success('Section updated successfully');
      resetForm();
      return updatedSections;
    }

    const newSection = {
      ...contentSectionForm,
      id: `temp_${Date.now()}`,
      order: currentSections.length,
    };
    toast.success('Section added successfully');
    resetForm();
    return [...currentSections, newSection];
  };

  const editContentSection = (section) => {
    // Merge onto INITIAL_FORM so newer fields (checklist_data, poll_data)
    // are always present even if the stored section is older.
    setContentSectionForm({ ...INITIAL_FORM, ...section });
    setEditingSectionId(section.id);
    toast.info('Editing section...');
  };

  const cancelEditSection = () => {
    setEditingSectionId(null);
    resetForm();
    toast.info('Edit cancelled');
  };

  const removeContentSection = (sectionId, currentSections) => {
    const updatedSections = currentSections.filter(s => s.id !== sectionId);
    if (editingSectionId === sectionId) {
      setEditingSectionId(null);
      resetForm();
    }
    toast.success('Section removed');
    return updatedSections;
  };

  const moveContentSection = (index, direction, currentSections) => {
    const newSections = [...currentSections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return currentSections;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((section, idx) => { section.order = idx; });
    return newSections;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  return {
    contentSectionForm,
    setContentSectionForm,
    editingSectionId,
    expandedSections,
    setExpandedSections,
    addContentSection,
    editContentSection,
    cancelEditSection,
    removeContentSection,
    moveContentSection,
    toggleSection,
    resetForm,
  };
};
